// Typed wrappers over the Phase 6 agent endpoints. Thin shims over apiFetch so
// the copilot + inbox pages don't repeat path/shape literals.

import { toDataSourceRequestString } from '@progress/kendo-data-query';
import type { State } from '@progress/kendo-data-query';
import type {
  AgentInboxItem,
  AgentRespondRequest,
  AgentRunDetail,
  AgentRunSummary,
  ConvertRunToWorkflowRequest,
  DataSourceResultDto,
  StartAgentRunRequest,
  StartAgentRunResponse,
  WorkflowDetail,
} from '@shared/api-types';
import { apiFetch } from './apiClient';

/**
 * Start an ad-hoc copilot run. Returns the new run id, plus the extensions the
 * server picked when the run was started in "Auto" scope. An Auto start the server
 * refuses (a goal that changes data, no fit) throws an ApiError with status 422
 * whose body is `{ message, autoSelection }`.
 */
export function startAgentRun(body: StartAgentRunRequest): Promise<StartAgentRunResponse> {
  return apiFetch<StartAgentRunResponse>('/api/agents/runs', { method: 'POST', body });
}

/**
 * List the tenant's agent runs (canvas + copilot) for the Copilot run-history
 * grid. Serializes the KendoReact grid's data-state with
 * `toDataSourceRequestString` (mirrors AllRunHistory.tsx) so the API's Kendo.Mvc
 * DataSourceRequest binder applies the server-side paging / sort / filter.
 */
export function listAgentRuns(
  dataState: State,
  signal?: AbortSignal,
): Promise<DataSourceResultDto<AgentRunSummary>> {
  const qs = toDataSourceRequestString(dataState);
  return apiFetch<DataSourceResultDto<AgentRunSummary>>(`/api/agents/runs?${qs}`, { signal });
}

/** Read one agent run with its loop turns. */
export function getAgentRun(id: string): Promise<AgentRunDetail> {
  return apiFetch<AgentRunDetail>(`/api/agents/runs/${id}`);
}

/**
 * Convert a past AI Playground run into a new draft workflow: the server seeds a
 * start → agent graph from the run (goal, model, and the same extension scope) and
 * returns the created workflow, which the caller opens in the designer.
 */
export function convertRunToWorkflow(
  id: string,
  body: ConvertRunToWorkflowRequest,
): Promise<WorkflowDetail> {
  return apiFetch<WorkflowDetail>(`/api/agents/runs/${id}/convert-to-workflow`, {
    method: 'POST',
    body,
  });
}

/**
 * Reseed the DB-authoritative action-required inbox. The single source of truth
 * for the inbox — called on load, on manual refresh, and on every SignalR
 * (re)connect.
 */
export function getAgentInbox(): Promise<AgentInboxItem[]> {
  return apiFetch<AgentInboxItem[]>('/api/agents/inbox');
}

/** Respond to a parked run (answer / approve / deny) and re-dispatch a continuation. */
export function respondToAgentRun(id: string, body: AgentRespondRequest): Promise<void> {
  return apiFetch<void>(`/api/agents/runs/${id}/respond`, { method: 'POST', body });
}

/**
 * Soft-cancel an in-flight copilot/inbox run. The run flips to Cancelled
 * immediately and leaves the action-required inbox; a runner still executing has
 * its eventual callback discarded.
 */
export function cancelAgentRun(id: string): Promise<void> {
  return apiFetch<void>(`/api/agents/runs/${id}/cancel`, { method: 'POST' });
}

/**
 * Cancel a whole workflow run. Used by the inbox for a canvas-bound
 * AgentActivity item: the agent-run cancel endpoint refuses those (409) because
 * the run is tied to a suspended workflow bookmark, so cancelling here cascades
 * to the parked child agent run and finalizes the bookmark in one transaction.
 */
export function cancelWorkflowRun(workflowId: string, runId: string): Promise<void> {
  return apiFetch<void>(`/api/workflows/${workflowId}/runs/${runId}/cancel`, { method: 'POST' });
}
